import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const MemoryDashboard = ({ userId = "7524308795" }) => {
  const [stats, setStats] = useState(null);
  const [facts, setFacts] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [contextPreview, setContextPreview] = useState("");

  const fetchMemoryData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, factsRes, decisionsRes] = await Promise.all([
        axios.get(`${API}/chronos/stats/${userId}`),
        axios.get(`${API}/chronos/facts/${userId}?limit=20`),
        axios.get(`${API}/chronos/decisions/${userId}?limit=10`)
      ]);

      setStats(statsRes.data);
      setFacts(factsRes.data.facts || []);
      setDecisions(decisionsRes.data.decisions || []);
    } catch (error) {
      console.error("Error fetching memory data:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchMemoryData();
  }, [fetchMemoryData]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/chronos/search/${userId}?query=${encodeURIComponent(searchQuery)}&limit=10`);
      setSearchResults(response.data.memories || []);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const handleConsolidate = async () => {
    setConsolidating(true);
    try {
      const response = await axios.post(`${API}/chronos/consolidate/${userId}`);
      if (response.data.status === "success") {
        alert(`Consolidação concluída! Factos: ${response.data.stats?.facts || 0}, Preferências: ${response.data.stats?.preferences || 0}, Decisões: ${response.data.stats?.decisions || 0}`);
        fetchMemoryData();
      } else {
        alert(`Consolidação: ${response.data.reason || "sem memórias suficientes"}`);
      }
    } catch (error) {
      console.error("Consolidation error:", error);
      alert("Erro na consolidação");
    } finally {
      setConsolidating(false);
    }
  };

  const handlePreviewContext = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await axios.get(`${API}/chronos/context/${userId}?message=${encodeURIComponent(searchQuery)}`);
      setContextPreview(response.data.context || "Sem contexto disponível");
    } catch (error) {
      console.error("Context preview error:", error);
    }
  };

  const getMemoryTypeColor = (type) => {
    const colors = {
      fact: "bg-blue-500/20 text-blue-300 border-blue-500/30",
      preference: "bg-purple-500/20 text-purple-300 border-purple-500/30",
      decision: "bg-amber-500/20 text-amber-300 border-amber-500/30",
      consolidated: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
    };
    return colors[type] || "bg-gray-500/20 text-gray-300 border-gray-500/30";
  };

  const getMemoryTypeIcon = (type) => {
    const icons = {
      fact: "📌",
      preference: "💡",
      decision: "🎯",
      consolidated: "✨"
    };
    return icons[type] || "📝";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-lg">A carregar memórias CHRONOS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              🧠 CHRONOS Memory Dashboard
            </h1>
            <p className="text-slate-400 mt-1">Sistema de Memória Persistente Vetorial</p>
          </div>
          <button
            onClick={handleConsolidate}
            disabled={consolidating}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {consolidating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                A consolidar...
              </>
            ) : (
              <>
                ✨ Consolidar Memórias
              </>
            )}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-5">
            <div className="text-3xl font-bold text-cyan-400">{stats?.raw_memories || 0}</div>
            <div className="text-slate-400 text-sm">Memórias Brutas</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-5">
            <div className="text-3xl font-bold text-purple-400">{stats?.consolidated_memories || 0}</div>
            <div className="text-slate-400 text-sm">Consolidadas</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-5">
            <div className="text-3xl font-bold text-emerald-400">{stats?.chromadb_messages || 0}</div>
            <div className="text-slate-400 text-sm">Vectores</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-5">
            <div className="text-3xl font-bold text-amber-400">{stats?.chromadb_facts || 0}</div>
            <div className="text-slate-400 text-sm">Factos</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-slate-800/30 p-1 rounded-xl w-fit">
          {[
            { id: "overview", label: "Visão Geral", icon: "📊" },
            { id: "search", label: "Pesquisa Semântica", icon: "🔍" },
            { id: "facts", label: "Factos", icon: "📋" },
            { id: "decisions", label: "Decisões", icon: "🎯" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id 
                  ? "bg-cyan-600 text-white" 
                  : "text-slate-400 hover:text-white hover:bg-slate-700/50"
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-slate-800/30 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold mb-4">📊 Tags Mais Comuns</h2>
              {stats?.top_tags && Object.keys(stats.top_tags).length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.top_tags).map(([tag, count]) => (
                    <div key={tag} className="px-4 py-2 bg-slate-700/50 rounded-full border border-slate-600/50 flex items-center gap-2">
                      <span className="text-cyan-400 font-medium">{tag}</span>
                      <span className="text-xs text-slate-400 bg-slate-600/50 px-2 py-0.5 rounded-full">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">Nenhuma tag disponível</p>
              )}

              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4">✨ Últimos Factos Consolidados</h2>
                {facts.length > 0 ? (
                  <div className="space-y-3">
                    {facts.slice(0, 5).map((fact, i) => (
                      <div key={i} className={`p-4 rounded-xl border ${getMemoryTypeColor(fact.memory_type)}`}>
                        <div className="flex items-start gap-3">
                          <span className="text-xl">{getMemoryTypeIcon(fact.memory_type)}</span>
                          <div className="flex-1">
                            <p className="text-slate-200">{fact.content}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              Tipo: {fact.memory_type} • Score: {(fact.importance_score * 10).toFixed(1)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">Nenhum facto consolidado. Clique em "Consolidar Memórias" para extrair factos das conversas.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === "search" && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">🔍 Pesquisa Semântica</h2>
              <p className="text-slate-400">Pesquise memórias por similaridade semântica (não apenas palavras-chave)</p>
              
              <div className="flex gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Ex: investimento, companhia, trabalho..."
                  className="flex-1 px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 text-white placeholder-slate-500"
                />
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 rounded-xl font-medium transition-all disabled:opacity-50"
                >
                  {searching ? "..." : "Pesquisar"}
                </button>
                <button
                  onClick={handlePreviewContext}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-xl font-medium transition-all"
                >
                  Ver Contexto
                </button>
              </div>

              {contextPreview && (
                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-xl">
                  <h3 className="text-sm font-medium text-purple-300 mb-2">Contexto que seria injetado no LLM:</h3>
                  <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">{contextPreview}</pre>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-3 mt-6">
                  <h3 className="text-lg font-medium text-slate-300">Resultados ({searchResults.length})</h3>
                  {searchResults.map((mem, i) => (
                    <div key={i} className="p-4 bg-slate-700/30 rounded-xl border border-slate-600/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-1 rounded text-xs ${mem.role === 'user' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300'}`}>
                          {mem.role === 'user' ? '👤 Utilizador' : '🤖 Dalpha'}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-emerald-400">
                            Relevância: {(mem.relevance_score * 100).toFixed(0)}%
                          </span>
                          <span className="text-xs text-slate-500">
                            {mem.timestamp?.slice(0, 10)}
                          </span>
                        </div>
                      </div>
                      <p className="text-slate-300">{mem.content_raw}</p>
                      {mem.metadata_tags?.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {mem.metadata_tags.filter(t => t).map((tag, j) => (
                            <span key={j} className="text-xs px-2 py-0.5 bg-slate-600/50 rounded-full text-slate-400">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "facts" && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">📋 Todos os Factos Consolidados ({facts.length})</h2>
              {facts.length > 0 ? (
                <div className="space-y-3">
                  {facts.map((fact, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${getMemoryTypeColor(fact.memory_type)}`}>
                      <div className="flex items-start gap-3">
                        <span className="text-xl">{getMemoryTypeIcon(fact.memory_type)}</span>
                        <div className="flex-1">
                          <p className="text-slate-200">{fact.content}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>Tipo: {fact.memory_type}</span>
                            <span>Importância: {(fact.importance_score * 10).toFixed(1)}/10</span>
                            <span>Acessos: {fact.access_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="text-6xl">🧠</span>
                  <p className="text-slate-400 mt-4">Nenhum facto consolidado ainda.</p>
                  <p className="text-slate-500 text-sm mt-2">Clique em "Consolidar Memórias" para extrair factos das suas conversas.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "decisions" && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">🎯 Decisões Importantes ({decisions.length})</h2>
              {decisions.length > 0 ? (
                <div className="space-y-3">
                  {decisions.map((dec, i) => (
                    <div key={i} className="p-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
                      <div className="flex items-start gap-3">
                        <span className="text-xl">🎯</span>
                        <div className="flex-1">
                          <p className="text-slate-200">{dec.content}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            Criado: {dec.created_at?.slice(0, 10)} • Importância: {(dec.importance_score * 10).toFixed(1)}/10
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="text-6xl">🎯</span>
                  <p className="text-slate-400 mt-4">Nenhuma decisão registada.</p>
                  <p className="text-slate-500 text-sm mt-2">As decisões importantes são extraídas durante a consolidação de memórias.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* COGNITO System Info */}
        <div className="mt-8 p-4 bg-slate-800/20 rounded-xl border border-slate-700/30">
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span>CHRONOS v2.0 • ChromaDB Vetorial • Sentence Transformers</span>
            <span className="mx-2">|</span>
            <span>User ID: {userId}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryDashboard;
